import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useNavigate } from 'react-router';
import { z } from 'zod';
import { Button, Card, CardBody, CardTitle, Field } from '../../ui';
import { supabase } from '../../shared/supabase/client';
import { poweredBy } from '../platform';
import { useSession } from '../session/SessionProvider';

/**
 * The coach signs in with a password (design.md F5).
 *
 * Her account was created by the owner's runbook, not by a sign-up form —
 * `enable_signup = false` means there is no form to abuse. The failure message
 * is one message for every cause: a wrong password and an address that has no
 * account must be indistinguishable, or the form becomes a way to ask whether
 * a given person coaches on this platform.
 */

const schema = z.object({
  email: z.email('That is not an address.').trim().min(1, 'Enter your email address.'),
  password: z.string().min(1, 'Enter your password.'),
});

type Values = z.infer<typeof schema>;

const SIGN_IN_FAILED = 'That email address and password do not match an account.';

export function SignInPage() {
  const { status, profile, profileLoaded } = useSession();
  const navigate = useNavigate();
  const [failure, setFailure] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (status === 'authenticated' && profileLoaded && profile?.role === 'coach') {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });
    if (error) {
      setFailure(SIGN_IN_FAILED);
      return;
    }
    await navigate('/admin', { replace: true });
  });

  const platform = poweredBy();

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-4 py-10">
      <Card>
        <CardTitle>Sign in</CardTitle>
        <CardBody>Coaching admin. Accounts are created for you, not signed up for.</CardBody>

        <form className="mt-6 flex flex-col gap-4" onSubmit={(event) => void onSubmit(event)}>
          <Field
            label="Email"
            type="email"
            autoComplete="username"
            error={errors.email?.message}
            {...register('email')}
          />
          <Field
            label="Password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          {failure === null ? null : (
            <p role="alert" className="text-danger text-sm">
              {failure}
            </p>
          )}

          <Button type="submit" size="lg" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Card>

      <footer className="text-ink-muted mt-6 text-center text-xs">
        {platform === null ? null : <p>{platform}</p>}
      </footer>
    </main>
  );
}
